// Configuration constants
export const PB_BASE_URL = 'https://onto-trouble-bpa-client-hub-websites.pockethost.io';
export const CLIENT_LOGIN_URL = window.location.origin + '/login';
export const PUBLIC_SITE_BASE_URL = import.meta.env.VITE_PUBLIC_SITE_BASE_URL || 'https://bpas-sites.netlify.app';

// Support Hub Configuration
export const SUPPORT_PB_URL = "https://report-topic-bpa-sol-support-hub.pockethost.io";
export const SUPPORT_APP_ID = "bpas_client_hub";
export const SUPPORT_APP_NAME = "BPAS Client Hub";
export const SUPPORT_ENVIRONMENT = "Prod";
export const SUPPORT_APP_VERSION = "1.0.0";
export const SUPPORT_PAGE_ROUTE = "/support";

// Status options
export const ENQUIRY_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' }
];

export const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' }
];

export const CHANGE_REQUEST_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' }
];

export const BLOG_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' }
];

export const SYSTEM_ROLES = Object.freeze({
  SUPER_ADMIN: 'super_admin',
  BPAS_ADMIN: 'bpas_admin',
  OWNER: 'owner',
  STAFF: 'staff',
  LEGACY_CLIENT_ADMIN: 'client_admin'
});

export const normalizeUserRole = (role) => String(role || '').trim().toLowerCase();

export const isSuperAdminRole = (role) =>
  normalizeUserRole(role) === SYSTEM_ROLES.SUPER_ADMIN;

export const isPlatformAdminRole = (role) => {
  const normalized = normalizeUserRole(role);
  return normalized === SYSTEM_ROLES.SUPER_ADMIN || normalized === SYSTEM_ROLES.BPAS_ADMIN;
};

export const isTenantAdminRole = (role) => {
  const normalized = normalizeUserRole(role);
  return normalized === SYSTEM_ROLES.OWNER || normalized === SYSTEM_ROLES.LEGACY_CLIENT_ADMIN;
};

export const canManageEnquiriesRole = (role) =>
  isPlatformAdminRole(role) || isTenantAdminRole(role);

export const getUserRoleLabel = (role) => {
  switch (normalizeUserRole(role)) {
    case SYSTEM_ROLES.SUPER_ADMIN: return 'Super Admin';
    case SYSTEM_ROLES.BPAS_ADMIN: return 'BPAS Admin';
    case SYSTEM_ROLES.OWNER:
    case SYSTEM_ROLES.LEGACY_CLIENT_ADMIN: return 'Client Admin';
    case SYSTEM_ROLES.STAFF: return 'Client User';
    default: return role || 'Unknown';
  }
};

// PocketBase-backed roles that may be assigned through User Management.
// super_admin is deliberately excluded because it is a reserved system role.
export const USER_ROLES = [
  { value: SYSTEM_ROLES.OWNER, label: 'Owner' },
  { value: SYSTEM_ROLES.STAFF, label: 'Staff' },
  { value: SYSTEM_ROLES.BPAS_ADMIN, label: 'BPAS Admin' }
];

export const USER_ROLE_FILTER_OPTIONS = [
  { value: SYSTEM_ROLES.SUPER_ADMIN, label: 'Super Admin (system)' },
  ...USER_ROLES
];

export const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

// Enquiry types
export const ENQUIRY_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'quote', label: 'Quote Request' },
  { value: 'booking', label: 'Booking' },
  { value: 'service_request', label: 'Service Request' }
];

export const normalizeSourceChannelForDisplay = (source) => {
  if (!source) return 'Other';
  const s = String(source).trim().toLowerCase();
  if (s.includes('linkedin') || s.includes('linked in')) return 'LinkedIn';
  if (s.includes('airtasker') || s.includes('air tasker')) return 'Airtasker';
  if (s.includes('facebook')) return 'Facebook';
  if (s.includes('instagram')) return 'Instagram';
  if (s === 'web' || s.includes('website')) return 'Website';
  if (s.includes('refer')) return 'Referral';
  if (s.includes('google')) return 'Google';
  if (s.includes('word_of_mouth') || s.includes('word of mouth')) return 'Word of Mouth';
  if (s.includes('client hub') || s.includes('client_hub') || s === 'manual') return 'Client Hub (Manual)';
  if (s.includes('email')) return 'Email';
  if (s.includes('phone')) return 'Phone';
  if (s.includes('sms')) return 'SMS';
  return 'Other';
};

// Source channels (Alphabetically sorted)
export const SOURCE_CHANNELS = [
  "Airtasker",
  "Client Hub (Manual)",
  "Email",
  "Facebook",
  "Google",
  "Instagram",
  "LinkedIn",
  "Other",
  "Phone",
  "Referral",
  "SMS",
  "Website",
  "Word of Mouth"
];